import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileCheck, Eye, FileText, Download, Search, CheckCircle, Clock, AlertTriangle, X, User, MapPin, Briefcase, GraduationCap, FileImage, Info, History, PlusCircle, ShieldCheck, UserPlus, FileCheck2, AlertCircle, Check, CheckCheck, Send, Mail, Loader2, StopCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDateDDMMYYYY, formatDateDDMonYYYY } from '@/utils/textFormatting';
import AuditTrail from '@/components/shared/AuditTrail';
import ChangePackageModal from './ChangePackageModal';

// Component to show all documents from both embedded and documents collection
const CaseDocumentsList = ({ viewEmployee, viewCase }) => {
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadDocs = async () => {
      setLoading(true);
      try {
        const allDocs = [...(viewEmployee?.documents || [])];
        
        // Collect proof_documents from checks
        if (viewCase?.checks) {
          viewCase.checks.forEach(check => {
            (check.proof_documents || []).forEach(doc => {
              allDocs.push({
                name: doc.filename || doc.description || doc.type,
                document_type: doc.type || doc.description || 'Document',
                url: doc.url,
              });
            });
          });
        }
        
        // Fetch from documents collection
        if (viewEmployee?.id) {
          try {
            const res = await axios.get(`/documents?employee_id=${viewEmployee.id}`);
            const collDocs = res.data || [];
            collDocs.forEach(d => {
              const exists = allDocs.some(existing => existing.url === d.file_url || existing.id === d.id);
              if (!exists) {
                allDocs.push({
                  id: d.id,
                  name: d.original_filename || d.file_name || d.document_type,
                  document_type: d.document_type,
                  url: d.file_url,
                });
              }
            });
          } catch (e) { /* ignore */ }
        }
        
        // Add declaration_form_url from employee if not already in docs
        if (viewEmployee?.declaration_form_url) {
          const hasDeclDoc = allDocs.some(d => d.document_type === 'declaration_form');
          if (!hasDeclDoc) {
            allDocs.push({
              id: 'declaration_form',
              name: 'Candidate Declaration Form',
              document_type: 'declaration_form',
              url: viewEmployee.declaration_form_url,
            });
          }
        }
        
        setDocs(allDocs);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    };
    if (viewEmployee) loadDocs();
  }, [viewEmployee?.id, viewCase?.id]);

  if (loading) return <div className="text-center py-4 text-slate-500">Loading documents...</div>;
  
  return docs.length > 0 ? (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {docs.map((doc, idx) => (
        <div key={idx} className={`p-3 border rounded-lg flex items-center gap-3 ${doc.document_type === 'declaration_form' ? 'border-indigo-200 bg-indigo-50/30' : ''}`}>
          <FileImage className={`w-8 h-8 shrink-0 ${doc.document_type === 'declaration_form' ? 'text-indigo-500' : 'text-slate-400'}`} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{doc.name || doc.document_type || `Document ${idx + 1}`}</p>
            <p className="text-xs text-slate-500 truncate">{doc.document_type || 'Document'}</p>
          </div>
          {doc.url && (
            <Button variant="ghost" size="sm" onClick={() => window.open(doc.url, '_blank')}>
              <Eye className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  ) : (
    <div className="text-center py-8 text-slate-500">
      <FileImage className="w-8 h-8 mx-auto mb-2 text-slate-300" />
      No documents uploaded
    </div>
  );
};

import ReportPreviewDialog from '@/components/reports/ReportPreviewDialog';

const CHECK_NAMES = {
  'address_check': 'Address',
  'criminal_court_check': 'Criminal Court',
  'id_check': 'ID Check',
  'employment_check': 'Employment',
  'education_check': 'Education',
  'credit_check': 'Credit',
  'reference_check': 'Reference',
  'drug_test': 'Drug Test',
  'global_database': 'Global Database'
};

const STATUS_CONFIG = {
  'draft': { label: 'Draft', bg: 'bg-slate-100 text-slate-700' },
  'initiated': { label: 'Draft', bg: 'bg-slate-100 text-slate-700' },
  'not_initiated': { label: 'Not Initiated', bg: 'bg-gray-100 text-gray-600' },
  'details_filled': { label: 'Details Filled', bg: 'bg-cyan-100 text-cyan-700' },
  'pending_submission': { label: 'Pending Submission', bg: 'bg-slate-100 text-slate-700' },
  'pending_client_approval': { label: 'Pending Client Approval', bg: 'bg-indigo-100 text-indigo-700' },
  'pending_qc': { label: 'Pending QC', bg: 'bg-amber-100 text-amber-700' },
  'pending_ops_assignment': { label: 'Awaiting Assignment', bg: 'bg-orange-100 text-orange-700' },
  'qc_approved': { label: 'QC Approved', bg: 'bg-blue-100 text-blue-700' },
  'approved': { label: 'Approved', bg: 'bg-blue-100 text-blue-700' },
  'in_progress': { label: 'In Progress', bg: 'bg-blue-100 text-blue-700' },
  'insufficiency': { label: 'Insufficiency', bg: 'bg-amber-100 text-amber-700' },
  'completed': { label: 'Completed', bg: 'bg-emerald-100 text-emerald-700' },
  'final_report_qc': { label: 'Final Report QC', bg: 'bg-purple-100 text-purple-700' },
  'report_approved': { label: 'Report Approved', bg: 'bg-green-100 text-green-700' },
  'on_hold': { label: 'On Hold', bg: 'bg-rose-100 text-rose-700' },
  'stopped': { label: 'Stopped', bg: 'bg-red-100 text-red-700' }
};

const RESULT_CONFIG = {
  'pending': { label: 'Pending', bg: 'bg-slate-100 text-slate-700' },
  'clear': { label: 'Clear', bg: 'bg-emerald-100 text-emerald-700' },
  'green': { label: 'Clear', bg: 'bg-emerald-100 text-emerald-700' },
  'discrepancy': { label: 'Discrepancy', bg: 'bg-rose-100 text-rose-700' },
  'minor_discrepancy': { label: 'Minor Discrepancy', bg: 'bg-orange-100 text-orange-700' },
  'red': { label: 'Discrepancy', bg: 'bg-rose-100 text-rose-700' },
  'insufficiency': { label: 'Insufficiency', bg: 'bg-amber-100 text-amber-700' },
  'unable_to_verify': { label: 'Unable to Verify', bg: 'bg-yellow-100 text-yellow-700' },
  'amber': { label: 'Unable to Verify', bg: 'bg-yellow-100 text-yellow-700' }
};

// Color mapping for "Completed" status badge based on verification result
const getCompletedStatusStyle = (overallResult) => {
  switch (overallResult) {
    case 'discrepancy':
    case 'major_discrepancy':
    case 'red':
      return { label: 'Completed', bg: 'bg-red-100 text-red-700' };
    case 'minor_discrepancy':
      return { label: 'Completed', bg: 'bg-rose-100 text-rose-600' };
    case 'unable_to_verify':
    case 'amber':
      return { label: 'Completed', bg: 'bg-amber-100 text-amber-700' };
    default:
      return STATUS_CONFIG['completed'];
  }
};

// Function to get display status based on actual submission state and check initiation
// Function to get display status based on check initiation state
const getDisplayStatus = (caseItem) => {
  if (!caseItem) return 'draft';
  
  // If case is in_progress or qc_approved, check if checks have been started
  if (['in_progress', 'qc_approved'].includes(caseItem.status)) {
    const checks = caseItem.checks || [];
    const hasAnyCheckStarted = checks.some(check => 
      check.assigned_at || check.status === 'in_progress' || check.status === 'vendor_assigned' || 
      check.status === 'verified' || check.status === 'completed' ||
      check.verification_progress === 'initiated' || check.verification_progress === 'in_progress'
    );
    if (!hasAnyCheckStarted && checks.length > 0) {
      return 'not_initiated';
    }
    if (caseItem.status === 'qc_approved' && hasAnyCheckStarted) {
      return 'in_progress';
    }
  }
  
  return caseItem.status;
};

export default function CasesTab() {
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewCase, setViewCase] = useState(null);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [reportEmailHistory, setReportEmailHistory] = useState([]);
  const [reportEmailHistoryLoading, setReportEmailHistoryLoading] = useState(false);
  
  // Bulk delete
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Send report
  const [sendingReport, setSendingReport] = useState(null); // case_id being sent
  const [changePkgCase, setChangePkgCase] = useState(null); // null OR case object → opens modal
  const [bulkSendConfirmOpen, setBulkSendConfirmOpen] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  
  // Stop Check state
  const [showStopCheck, setShowStopCheck] = useState(null);
  const [stopReason, setStopReason] = useState('');
  const [stopDescription, setStopDescription] = useState('');
  const [stopping, setStopping] = useState(false);
  
  const handleStopCheck = async () => {
    if (!showStopCheck || !stopReason) {
      toast.error('Please select a reason');
      return;
    }
    setStopping(true);
    try {
      await axios.put(`/cases/${showStopCheck.id}/stop-check`, {
        reason: stopReason,
        description: stopDescription || ''
      });
      toast.success('Case stopped successfully');
      setShowStopCheck(null);
      setStopReason('');
      setStopDescription('');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to stop case');
    } finally {
      setStopping(false);
    }
  };
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [completedDateFilter, setCompletedDateFilter] = useState('all');
  const [completedDateFrom, setCompletedDateFrom] = useState('');
  const [completedDateTo, setCompletedDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState('latest');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Jul 21, 2026 P0 defense: Promise.allSettled so one endpoint
      // failing (e.g., /api/employees 500 from a bad row on production)
      // doesn't blank the whole All Cases dashboard. Each dataset
      // populates if its fetch succeeded; failures are toasted per-endpoint.
      const [casesRes, employeesRes, clientsRes, entitiesRes] = await Promise.allSettled([
        axios.get('/cases'),
        axios.get('/employees'),
        axios.get('/clients'),
        axios.get('/client-entities')
      ]);
      const failed = [];
      if (casesRes.status === 'fulfilled') setCases(casesRes.value.data);
      else failed.push('cases');
      if (employeesRes.status === 'fulfilled') setEmployees(employeesRes.value.data);
      else failed.push('employees');
      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data);
      else failed.push('clients');
      if (entitiesRes.status === 'fulfilled') setEntities(entitiesRes.value.data);
      else failed.push('entities');
      if (failed.length > 0) {
        toast.error(`Failed to load: ${failed.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getEmployee = (employeeId) => employees.find(e => e.id === employeeId);
  const getClient = (clientId) => clients.find(c => c.id === clientId);
  const getEntity = (entityId) => entities.find(e => e.id === entityId);

  const getCaseDetails = (caseItem) => {
    const employee = getEmployee(caseItem.employee_id);
    // Fallback to case-level entity_id/client_id if employee lookup fails
    const entityId = employee?.entity_id || caseItem.entity_id;
    const entity = entityId ? getEntity(entityId) : null;
    const clientId = entity?.client_id || caseItem.client_id;
    const client = clientId ? getClient(clientId) : null;
    return { employee, entity, client };
  };

  const getFormProgress = (caseItem) => {
    const fp = caseItem.form_progress;
    if (!fp) return { completed: 0, total: 6, percentage: 0 };
    return { completed: fp.completed, total: fp.total, percentage: fp.percentage };
  };

  const getVerificationStatus = (checks) => {
    if (!checks || checks.length === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = checks.filter(c => ['verified', 'completed', 'clear', 'discrepancy', 'unable_to_verify', 'failed'].includes(c.status)).length;
    return { completed, total: checks.length, percentage: Math.round((completed / checks.length) * 100) };
  };

  const getOverallResult = (caseItem, checks) => {
    if (!checks || checks.length === 0) return 'pending';

    // Include NA-by-declaration / not_applicable / stop_check as terminal
    // "completed" statuses — without this a case with any NA-by-declaration
    // check stays "Pending" forever even when every other check is closed
    // (Feb 28, 2026 fix — matches backend calculate_case_overall_status).
    const TERMINAL_STATUSES = ['verified', 'completed', 'clear', 'discrepancy', 'unable_to_verify', 'failed', 'not_applicable', 'not_applicable_by_declaration', 'stop_check'];
    const completedChecks = checks.filter(c => TERMINAL_STATUSES.includes(c.status));
    
    // If not all checks are completed, check for negative results but default to pending
    if (completedChecks.length === 0) return 'pending';
    
    // Use verification_result field (fallback to result for backward compatibility)
    const getResult = (c) => c.verification_result || c.result;
    
    const anyDiscrepancy = completedChecks.some(c => 
      ['discrepancy', 'major_discrepancy', 'failed', 'red'].includes(getResult(c)) || 
      c.status === 'discrepancy' || c.status === 'failed'
    );
    const anyUnableToVerify = completedChecks.some(c => 
      c.status === 'unable_to_verify' || getResult(c) === 'unable_to_verify'
    );
    const anyInsuff = checks.some(c => c.has_insufficiency);
    
    // Negative results show immediately regardless of completion
    if (anyDiscrepancy) return 'discrepancy';
    if (anyUnableToVerify) return 'amber';
    if (anyInsuff) return 'insufficiency';
    
    // Only show clear/green when ALL checks are completed. NA-by-declaration
    // & stop_check count as green-neutral (candidate/admin opted out) so
    // they don't block the case from turning GREEN.
    const GREEN_NEUTRAL_RESULTS = ['clear', 'green', 'not_applicable', 'stop_check'];
    const GREEN_NEUTRAL_STATUSES = ['clear', 'not_applicable', 'not_applicable_by_declaration', 'stop_check'];
    const allCompleted = completedChecks.length === checks.length;
    const allClear = completedChecks.every(c => GREEN_NEUTRAL_RESULTS.includes(getResult(c)) || GREEN_NEUTRAL_STATUSES.includes(c.status));
    if (allClear && allCompleted) return 'clear';
    
    return 'pending';
  };

  // Date filter logic
  const filterByDate = (caseItem) => {
    if (dateFilter === 'all') return true;
    const caseDate = new Date(caseItem.created_at);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today':
        return caseDate >= today;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return caseDate >= yesterday && caseDate < today;
      case 'last_7_days':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        return caseDate >= last7;
      case 'last_30_days':
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        return caseDate >= last30;
      default:
        return true;
    }
  };

  // Completed date filter logic
  const filterByCompletedDate = (caseItem) => {
    if (completedDateFilter === 'all') return true;
    if (completedDateFilter === 'date_range' && !completedDateFrom && !completedDateTo) return true;
    if (!caseItem.completed_at) return false;
    const completedDate = new Date(caseItem.completed_at);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (completedDateFilter) {
      case 'today': {
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);
        return completedDate >= today && completedDate <= endOfToday;
      }
      case 'this_week': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return completedDate >= weekAgo;
      }
      case 'this_month': {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return completedDate >= monthAgo;
      }
      case 'date_range': {
        if (!completedDateFrom && !completedDateTo) return true;
        const from = completedDateFrom ? new Date(completedDateFrom) : new Date(0);
        const to = completedDateTo ? new Date(completedDateTo + 'T23:59:59') : new Date();
        return completedDate >= from && completedDate <= to;
      }
      default:
        return true;
    }
  };

  // Filtered and sorted cases
  const filteredCases = cases
    .filter(c => {
      const { employee, client } = getCaseDetails(c);
      const searchMatch = !searchTerm || 
        c.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client?.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const statusMatch = statusFilter === 'all' || getDisplayStatus(c) === statusFilter;
      const clientMatch = clientFilter === 'all' || getCaseDetails(c).client?.id === clientFilter;
      const dateMatch = filterByDate(c);
      const completedMatch = filterByCompletedDate(c);
      
      return searchMatch && statusMatch && clientMatch && dateMatch && completedMatch;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, clientFilter, dateFilter, completedDateFilter, completedDateFrom, completedDateTo, sortOrder]);

  const openViewCase = async (c) => {
    setViewCase(c);
    setActivityLog([]);
    setReportEmailHistory([]);
    const emp = getEmployee(c.employee_id);
    if (emp) {
      try {
        const r = await axios.get('/employees/' + emp.id);
        setViewEmployee(r.data);
      } catch (e) {
        setViewEmployee(emp);
      }
    } else if (c.employee_id) {
      // Employee not in local list — try fetching directly from API
      try {
        const r = await axios.get('/employees/' + c.employee_id);
        setViewEmployee(r.data);
      } catch (e) {
        // Build minimal employee from case data so the dialog still renders
        setViewEmployee({ name: c.employee_name, email: c.employee_email, id: c.employee_id });
      }
    }
    // Fetch activity log
    fetchActivityLog(c.id);
    fetchReportEmailHistory(c.id);
  };

  const fetchReportEmailHistory = async (caseId) => {
    setReportEmailHistoryLoading(true);
    try {
      const res = await axios.get(`/cases/${caseId}/report-email-history`);
      setReportEmailHistory(res.data?.history || []);
    } catch (error) {
      console.error('Failed to fetch report email history:', error);
      setReportEmailHistory([]);
    } finally {
      setReportEmailHistoryLoading(false);
    }
  };

  const fetchActivityLog = async (caseId) => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`/cases/${caseId}/activity-log`);
      setActivityLog(res.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
      setActivityLog([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const getActivityIcon = (iconName) => {
    const icons = {
      'plus-circle': <PlusCircle className="w-4 h-4" />,
      'check-circle': <CheckCircle className="w-4 h-4" />,
      'shield-check': <ShieldCheck className="w-4 h-4" />,
      'user-plus': <UserPlus className="w-4 h-4" />,
      'alert-triangle': <AlertTriangle className="w-4 h-4" />,
      'check': <Check className="w-4 h-4" />,
      'alert-circle': <AlertCircle className="w-4 h-4" />,
      'file-text': <FileText className="w-4 h-4" />,
      'check-square': <FileCheck2 className="w-4 h-4" />
    };
    return icons[iconName] || <Clock className="w-4 h-4" />;
  };

  const getActivityColor = (actionType) => {
    const colors = {
      'create': 'bg-blue-500',
      'submit': 'bg-emerald-500',
      'approve': 'bg-green-500',
      'assign': 'bg-indigo-500',
      'insufficiency': 'bg-amber-500',
      'clear': 'bg-teal-500',
      'complete': 'bg-emerald-600'
    };
    return colors[actionType] || 'bg-slate-500';
  };

  const generateReport = async (caseItem, reportType) => {
    try {
      toast.info(`Generating ${reportType} report...`);
      const response = await axios.post(`/cases/${caseItem.id}/generate-bgv-report`, {
        is_final: reportType === 'final'
      }, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      const prefix = reportType === 'final' ? 'FINAL' : 'INTERIM';
      link.download = match ? match[1] : `${prefix}_REPORT_${caseItem.id}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const formatDate = (d) => formatDateDDMMYYYY(d);

    const headers = [
      'Case ID', 'Emp ID',
      'Request Raised', 'Submitted', 'Date Completed', 'Final Report Date',
      'Candidate', 'Client', 'Entity',
      'Status', 'Result',
      'Package', 'Rate', 'Billed Month', 'Checks Conducted',
    ];

    const checksConducted = (c) => {
      const arr = c.checks || [];
      // Unique check_type labels, comma separated.
      const seen = new Set();
      const labels = [];
      arr.forEach(ch => {
        const t = (ch.check_type || '').replace(/_/g, ' ').trim();
        if (t && !seen.has(t)) { seen.add(t); labels.push(t); }
      });
      return labels.join(', ');
    };

    const fmtRate = (v) => {
      if (v === null || v === undefined || v === '') return '';
      const n = Number(v);
      if (isNaN(n)) return String(v);
      return n.toFixed(2);
    };

    const rows = filteredCases.map(c => {
      const { employee, client, entity } = getCaseDetails(c);
      const formProgress = getFormProgress(c);
      const displayStatus = getDisplayStatus(c);
      const isSubmitted = c.employee_submitted || c.source === 'bulk_import' || c.source === 'data_entry';

      return [
        c.id || '',
        c.employee_code || employee?.employee_code || '',
        formatDate(c.invitation_sent_at || c.created_at),
        c.submitted_at ? formatDate(c.submitted_at) : (isSubmitted ? formatDate(c.created_at) : 'Pending'),
        formatDate(c.completed_at),
        formatDate(c.final_report_sent_at),
        employee?.name || '',
        client?.company_name || '',
        entity?.entity_name || '',
        STATUS_CONFIG[displayStatus]?.label || displayStatus,
        RESULT_CONFIG[getOverallResult(c, c.checks)]?.label || 'Pending',
        c.package_name || '',
        fmtRate(c.billed_rate),
        c.billed_month || '',
        checksConducted(c),
      ];
    });
    
    const csv = [headers, ...rows].map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verification_cases_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export completed');
  };

  const toggleSelectCase = (caseId) => {
    setSelectedCases(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCases.size === paginatedCases.length) {
      setSelectedCases(new Set());
    } else {
      setSelectedCases(new Set(paginatedCases.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCases.size === 0) return;
    setDeleting(true);
    try {
      const res = await axios.post('/cases/bulk-delete', {
        case_ids: Array.from(selectedCases)
      });
      toast.success(`Deleted ${res.data.deleted_count} case(s) and all related data`);
      setSelectedCases(new Set());
      setDeleteConfirmOpen(false);
      // Refresh data
      const casesRes = await axios.get('/cases');
      setCases(casesRes.data?.cases || casesRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete cases');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendReportEmail = async (caseItem) => {
    setSendingReport(caseItem.id);
    try {
      const res = await axios.post(`/cases/${caseItem.id}/send-report-email`);
      toast.success(`${res.data.report_type} report sent to ${res.data.to_email}`);
      // Update local case data to show "sent" indicator
      setCases(prev => prev.map(c => c.id === caseItem.id ? {...c, completion_email_sent: true, completion_email_sent_at: new Date().toISOString(), completion_email_sent_to: res.data.to_email} : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send report email');
    } finally {
      setSendingReport(null);
    }
  };

  const handleBulkSendReport = async () => {
    if (selectedCases.size === 0) return;
    setBulkSending(true);
    try {
      const res = await axios.post('/cases/bulk-send-report-email', {
        case_ids: Array.from(selectedCases)
      });
      toast.success(`${res.data.sent} report(s) sent, ${res.data.failed} failed`);
      // Update local case data for sent cases
      const sentIds = new Set((res.data.results || []).filter(r => r.status === 'sent').map(r => r.case_id));
      setCases(prev => prev.map(c => sentIds.has(c.id) ? {...c, completion_email_sent: true, completion_email_sent_at: new Date().toISOString()} : c));
      setSelectedCases(new Set());
      setBulkSendConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send bulk reports');
    } finally {
      setBulkSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Verification Cases</h2>
          <p className="text-slate-500">{filteredCases.length} cases found</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        {selectedCases.size > 0 && (
          <div className="flex gap-2">
            <Button 
              variant="default" 
              onClick={() => setBulkSendConfirmOpen(true)}
              data-testid="bulk-send-report-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Report ({selectedCases.size})
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setDeleteConfirmOpen(true)}
              data-testid="bulk-delete-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Delete {selectedCases.size} Case(s)
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by Case ID, name, email, client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[200px]" data-testid="client-filter">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="not_initiated">Not Initiated</SelectItem>
                <SelectItem value="pending_submission">Pending Submission</SelectItem>
                <SelectItem value="pending_qc">Pending QC</SelectItem>
                <SelectItem value="qc_approved">QC Approved</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="insufficiency">Insufficiency</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="final_report_qc">Final Report QC</SelectItem>
                <SelectItem value="report_approved">Report Approved</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="stopped">Stopped</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[180px]" data-testid="date-filter">
                <SelectValue placeholder="Submitted Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Submitted: All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                <SelectItem value="last_30_days">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={completedDateFilter} onValueChange={(val) => { setCompletedDateFilter(val); if (val !== 'date_range') { setCompletedDateFrom(''); setCompletedDateTo(''); } }}>
              <SelectTrigger className="w-[180px]" data-testid="completed-date-filter">
                <SelectValue placeholder="Completed Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Completed: All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="date_range">Date Range</SelectItem>
              </SelectContent>
            </Select>
            {completedDateFilter === 'date_range' && (
              <div className="flex items-center gap-1.5">
                <input type="date" value={completedDateFrom} onChange={e => setCompletedDateFrom(e.target.value)} className="h-9 px-2 text-sm border rounded-md border-slate-200" data-testid="completed-date-from" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={completedDateTo} onChange={e => setCompletedDateTo(e.target.value)} className="h-9 px-2 text-sm border rounded-md border-slate-200" data-testid="completed-date-to" />
              </div>
            )}
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[150px]" data-testid="sort-order">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      {filteredCases.length > 0 ? (
        <Card className="border-slate-200 shadow-sm">
          {/* Pagination Controls - Above Table */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50" data-testid="pagination-top">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredCases.length)} of {filteredCases.length} cases
              </span>
              <Select value={itemsPerPage.toString()} onValueChange={(val) => { setItemsPerPage(parseInt(val)); setCurrentPage(1); }}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</Button>
              <span className="px-3 py-1 text-sm font-medium">Page {currentPage} of {totalPages || 1}</span>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>Last</Button>
            </div>
          </div>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all-cases"
                      checked={paginatedCases.length > 0 && selectedCases.size === paginatedCases.length}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300"
                    />
                  </TableHead>
                  <TableHead className="font-semibold">Case ID</TableHead>
                  <TableHead className="font-semibold">Request Raised</TableHead>
                  <TableHead className="font-semibold">Submitted</TableHead>
                  <TableHead className="font-semibold">Date Completed</TableHead>
                  <TableHead className="font-semibold">Candidate</TableHead>
                  <TableHead className="font-semibold">Client / Entity</TableHead>
                  <TableHead className="font-semibold">Form Progress</TableHead>
                  <TableHead className="font-semibold">Overall Status</TableHead>
                  <TableHead className="font-semibold">Overall Result</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCases.map((caseItem) => {
                  const { employee, client, entity } = getCaseDetails(caseItem);
                  const formProgress = getFormProgress(caseItem);
                  const overallResult = getOverallResult(caseItem, caseItem.checks);
                  const displayStatus = getDisplayStatus(caseItem);
                  const baseStatusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG['initiated'];
                  const statusConfig = displayStatus === 'completed' ? getCompletedStatusStyle(overallResult) : baseStatusConfig;
                  const resultConfig = RESULT_CONFIG[overallResult] || RESULT_CONFIG['pending'];
                  const isSubmitted = caseItem.employee_submitted || caseItem.source === 'bulk_import' || caseItem.source === 'data_entry';
                  
                  return (
                    <TableRow key={caseItem.id} className="hover:bg-slate-50">
                      <TableCell>
                        <input
                          type="checkbox"
                          data-testid={`select-case-${caseItem.id}`}
                          checked={selectedCases.has(caseItem.id)}
                          onChange={() => toggleSelectCase(caseItem.id)}
                          className="rounded border-slate-300"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium">
                        {caseItem.id}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDateDDMMYYYY(caseItem.invitation_sent_at || caseItem.created_at)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {caseItem.submitted_at
                          ? formatDateDDMMYYYY(caseItem.submitted_at)
                          : (isSubmitted
                              ? formatDateDDMMYYYY(caseItem.created_at)
                              : <span className="text-slate-400">Pending</span>)
                        }
                      </TableCell>
                      <TableCell className="text-sm">
                        {caseItem.completed_at 
                          ? formatDateDDMMYYYY(caseItem.completed_at)
                          : <span className="text-slate-400">-</span>
                        }
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{employee?.name || caseItem.employee_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{employee?.email || caseItem.employee_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{client?.company_name || caseItem.client_name || '-'}</p>
                          <p className="text-xs text-slate-500">{entity?.entity_name || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${formProgress.percentage === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                              style={{ width: `${formProgress.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-700">{formProgress.completed}/{formProgress.total}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge data-testid={`case-status-badge-${caseItem.id}`} className={statusConfig.bg}>{statusConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge data-testid={`case-result-badge-${caseItem.id}`} className={resultConfig.bg}>{resultConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-center">
                          <Button variant="ghost" size="sm" onClick={() => openViewCase(caseItem)} title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setChangePkgCase(caseItem)}
                            title="Change Package"
                            data-testid={`change-package-${caseItem.id}`}
                          >
                            <RefreshCw className="w-4 h-4 text-indigo-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSendReportEmail(caseItem)} 
                            title={caseItem.updated_report_sent
                              ? `Updated report re-sent to ${caseItem.completion_email_sent_to || 'client'} on ${caseItem.last_updated_report_sent_at ? new Date(caseItem.last_updated_report_sent_at).toLocaleDateString() : (caseItem.completion_email_sent_at ? new Date(caseItem.completion_email_sent_at).toLocaleDateString() : '')} — Click to resend`
                              : (caseItem.completion_email_sent 
                                ? `${caseItem.final_report_qc_approved ? 'QC-approved report' : 'Report'} sent to ${caseItem.completion_email_sent_to || 'client'} on ${caseItem.completion_email_sent_at ? new Date(caseItem.completion_email_sent_at).toLocaleDateString() : ''} — Click to resend`
                                : "Send Report Email")}
                            disabled={sendingReport === caseItem.id}
                            data-testid={`send-report-${caseItem.id}`}
                            className={caseItem.completion_email_sent 
                              ? (caseItem.final_report_qc_approved
                                  ? "text-purple-600 hover:text-purple-800 hover:bg-purple-50 relative"
                                  : "text-green-600 hover:text-green-800 hover:bg-green-50 relative")
                              : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"}
                          >
                            {sendingReport === caseItem.id 
                              ? <Loader2 className="w-4 h-4 animate-spin" /> 
                              : caseItem.updated_report_sent
                                ? <><Send className="w-4 h-4" /><CheckCheck data-testid={`send-report-double-tick-${caseItem.id}`} className={`w-3 h-3 absolute -top-0.5 -right-0.5 bg-white rounded-full ${caseItem.final_report_qc_approved ? 'text-purple-600' : 'text-blue-600'}`} /></>
                                : caseItem.completion_email_sent 
                                  ? <><Send className="w-4 h-4" /><Check data-testid={`send-report-single-tick-${caseItem.id}`} className={`w-2.5 h-2.5 absolute -top-0.5 -right-0.5 bg-white rounded-full ${caseItem.final_report_qc_approved ? 'text-purple-600' : 'text-green-600'}`} /></>
                                  : <Send className="w-4 h-4" />}
                          </Button>
                          <ReportPreviewDialog 
                            caseId={caseItem.id} 
                            caseStatus={caseItem.status}
                            trigger={<Button variant="ghost" size="sm" title="Preview Report" data-testid={`preview-report-${caseItem.id}`}><FileCheck className="w-4 h-4" /></Button>}
                          />
                          {caseItem.final_report_qc_pending && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  if (!window.confirm('Approve and email the Final Report to the client now?')) return;
                                  try {
                                    await axios.post(`/cases/${caseItem.id}/final-report-qc/approve`);
                                    toast.success('Final report approved — dispatching to client');
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to approve');
                                  }
                                }}
                                title="Approve & Send to Client"
                                className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                data-testid={`qc-approve-inline-${caseItem.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  const reason = window.prompt('Reason for sending back (optional):', '');
                                  if (reason === null) return;
                                  try {
                                    await axios.post(`/cases/${caseItem.id}/final-report-qc/reject`, { reason });
                                    toast.success('Sent back for changes — case remains in Final Report QC');
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to reject');
                                  }
                                }}
                                title="Send Back for Changes"
                                className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                data-testid={`qc-reject-inline-${caseItem.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {['stopped', 'completed', 'report_approved', 'final_report_qc'].includes(caseItem.status) ? (
                            <Button variant="ghost" size="sm" onClick={() => generateReport(caseItem, 'interim')} title="Download Report" data-testid={`download-report-${caseItem.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setShowStopCheck(caseItem); setStopReason(''); setStopDescription(''); }}
                              title="Stop Check"
                              className="text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                              data-testid={`stop-check-allcases-${caseItem.id}`}
                            >
                              <StopCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="text-center py-12">
            <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">No cases found</p>
            <p className="text-sm text-slate-400">Try adjusting your filters</p>
          </CardContent>
        </Card>
      )}

      {/* Case Details Dialog with Tabs */}
      <Dialog open={!!viewCase} onOpenChange={() => { setViewCase(null); setViewEmployee(null); setActivityLog([]); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto [&>button.absolute]:z-50">
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Candidate Details - {viewEmployee?.name || 'Unknown'}
              </div>
              {viewCase && (() => {
                const dlgDisplayStatus = getDisplayStatus(viewCase);
                const dlgOverallResult = getOverallResult(viewCase, viewCase.checks);
                const dlgStatusStyle = dlgDisplayStatus === 'completed' ? getCompletedStatusStyle(dlgOverallResult) : (STATUS_CONFIG[dlgDisplayStatus] || { bg: 'bg-slate-100', label: viewCase?.status });
                return (
                  <Badge data-testid="dialog-status-badge" className={dlgStatusStyle.bg}>
                    {dlgStatusStyle.label}
                  </Badge>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {viewCase && viewEmployee && (
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="basic" className="flex items-center gap-1 text-xs">
                  <User className="w-3 h-3" />Basic
                </TabsTrigger>
                <TabsTrigger value="address" className="flex items-center gap-1 text-xs">
                  <MapPin className="w-3 h-3" />Address
                </TabsTrigger>
                <TabsTrigger value="employment" className="flex items-center gap-1 text-xs">
                  <Briefcase className="w-3 h-3" />Employment
                </TabsTrigger>
                <TabsTrigger value="education" className="flex items-center gap-1 text-xs">
                  <GraduationCap className="w-3 h-3" />Education
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex items-center gap-1 text-xs">
                  <FileImage className="w-3 h-3" />Documents
                </TabsTrigger>
                <TabsTrigger value="case" className="flex items-center gap-1 text-xs">
                  <Info className="w-3 h-3" />Case Info
                </TabsTrigger>
                <TabsTrigger value="activity" className="flex items-center gap-1 text-xs">
                  <History className="w-3 h-3" />Audit Trail
                </TabsTrigger>
              </TabsList>
              
              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div><span className="text-slate-500">Full Name:</span><br/><span className="font-medium">{viewEmployee.name || '-'}</span></div>
                      <div><span className="text-slate-500">Email:</span><br/><span className="font-medium">{viewEmployee.email || '-'}</span></div>
                      <div><span className="text-slate-500">Phone:</span><br/><span className="font-medium">{viewEmployee.phone || '-'}</span></div>
                      <div><span className="text-slate-500">Date of Birth:</span><br/><span className="font-medium">{viewEmployee.dob || viewEmployee.date_of_birth || '-'}</span></div>
                      <div><span className="text-slate-500">Father's Name:</span><br/><span className="font-medium">{viewEmployee.father_name || '-'}</span></div>
                      <div><span className="text-slate-500">Gender:</span><br/><span className="font-medium capitalize">{viewEmployee.gender || '-'}</span></div>
                      <div><span className="text-slate-500">Marital Status:</span><br/><span className="font-medium capitalize">{viewEmployee.marital_status || '-'}</span></div>
                      <div><span className="text-slate-500">Nationality:</span><br/><span className="font-medium">{viewEmployee.nationality || 'Indian'}</span></div>
                      <div><span className="text-slate-500">Designation:</span><br/><span className="font-medium">{viewEmployee.designation || '-'}</span></div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">ID Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div><span className="text-slate-500">Aadhaar Number:</span><br/><span className="font-medium font-mono">{viewEmployee.aadhaar_number || viewEmployee.id_details?.aadhaar || '-'}</span></div>
                      <div><span className="text-slate-500">PAN Number:</span><br/><span className="font-medium font-mono">{viewEmployee.pan_number || viewEmployee.id_details?.pan || '-'}</span></div>
                      <div><span className="text-slate-500">Passport Number:</span><br/><span className="font-medium font-mono">{viewEmployee.passport_number || viewEmployee.id_details?.passport || '-'}</span></div>
                      <div><span className="text-slate-500">Driving License:</span><br/><span className="font-medium font-mono">{viewEmployee.driving_license || viewEmployee.id_details?.driving_license || '-'}</span></div>
                      <div><span className="text-slate-500">Voter ID:</span><br/><span className="font-medium font-mono">{viewEmployee.voter_id || viewEmployee.id_details?.voter_id || '-'}</span></div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Address Tab */}
              <TabsContent value="address" className="space-y-4 mt-4">
                {(() => {
                  // Build addresses array from present_address, permanent_address, and additional_addresses
                  const addresses = [];
                  if (viewEmployee.present_address && viewEmployee.present_address.complete_address) {
                    addresses.push({ ...viewEmployee.present_address, address_type: 'Present' });
                  }
                  if (viewEmployee.permanent_address && viewEmployee.permanent_address.complete_address) {
                    addresses.push({ ...viewEmployee.permanent_address, address_type: 'Permanent' });
                  }
                  if (viewEmployee.additional_addresses && viewEmployee.additional_addresses.length > 0) {
                    viewEmployee.additional_addresses.forEach((addr, i) => {
                      if (addr && addr.complete_address) {
                        addresses.push({ ...addr, address_type: `Additional ${i + 1}` });
                      }
                    });
                  }
                  // Fallback to legacy addresses array
                  if (addresses.length === 0 && viewEmployee.addresses && viewEmployee.addresses.length > 0) {
                    addresses.push(...viewEmployee.addresses);
                  }
                  
                  // Fallback to verified_details from address checks if employee has no address data
                  if (addresses.length === 0 && viewCase?.checks) {
                    viewCase.checks
                      .filter(c => c.check_type === 'address_check')
                      .forEach((check) => {
                        const vd = check.verified_details || check.subcheck_data || {};
                        if (Object.keys(vd).length > 0) {
                          addresses.push({
                            address_type: check.sub_option === 'present' ? 'Present' : check.sub_option === 'permanent' ? 'Permanent' : (check.sub_option || 'Address'),
                            complete_address: vd.full_address || vd.address || vd.complete_address || '-',
                            landmark: vd.landmark || '-',
                            city: vd.city || '-',
                            state: vd.state || '-',
                            pincode: vd.pincode || vd.pin_code || '-',
                            _fromChecks: true,
                            _checkData: vd
                          });
                        }
                      });
                  }
                  
                  return addresses.length > 0 ? (
                    addresses.map((addr, idx) => (
                    <Card key={idx}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Badge className={addr.address_type === 'Present' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                            {addr.address_type || 'Address'} {!addr.address_type && idx + 1}
                          </Badge>
                          {addr._fromChecks && <span className="text-xs text-slate-400 font-normal">(from verification)</span>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div className="col-span-2"><span className="text-slate-500">Full Address:</span><br/><span className="font-medium">{addr.complete_address || addr.full_address || addr.address_line1 || '-'}</span></div>
                          <div><span className="text-slate-500">Landmark:</span><br/><span className="font-medium">{addr.landmark || '-'}</span></div>
                          <div><span className="text-slate-500">City:</span><br/><span className="font-medium">{addr.city || '-'}</span></div>
                          <div><span className="text-slate-500">State:</span><br/><span className="font-medium">{addr.state || '-'}</span></div>
                          <div><span className="text-slate-500">PIN Code:</span><br/><span className="font-medium">{addr.pincode || addr.zip_code || '-'}</span></div>
                        </div>
                        {addr._checkData && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-slate-500 mb-2 font-medium">Verification Details</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                              {Object.entries(addr._checkData)
                                .filter(([k, v]) => v && !['verification_submitted','_id','full_address','address','complete_address','landmark','city','state','pincode','pin_code'].includes(k))
                                .slice(0, 9)
                                .map(([k, v]) => (
                                  <div key={k}><span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}:</span><br/><span className="font-medium">{String(v)}</span></div>
                                ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardContent className="text-center py-8 text-slate-500">
                      <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No address information available
                    </CardContent>
                  </Card>
                );
                })()}
              </TabsContent>
              
              {/* Employment Tab */}
              <TabsContent value="employment" className="space-y-4 mt-4">
                {(() => {
                  let employmentData = viewEmployee.employment_entries || viewEmployee.employment_history || [];
                  
                  // If latest_employment exists but employment_entries is stale/different, prefer data entry format
                  if (viewEmployee.latest_employment && (!employmentData.length || !employmentData.some(e => e.employer_name || e.employee_code))) {
                    employmentData = [viewEmployee.latest_employment, ...(viewEmployee.past_employments || [])];
                  }
                  
                  // Fallback to verified_details from employment checks
                  if (employmentData.length === 0 && viewCase?.checks) {
                    const empChecks = viewCase.checks.filter(c => c.check_type === 'employment_check');
                    employmentData = empChecks.map(check => {
                      const vd = check.verified_details || check.subcheck_data || {};
                      return {
                        company_name: vd.company_name || vd.organization_name || check.sub_option || '-',
                        designation: vd.designation || vd.position || '-',
                        employee_id: vd.employee_id || vd.employee_code || '-',
                        from_date: vd.from_date || vd.doj || vd.joining_date || '-',
                        to_date: vd.to_date || vd.dol || vd.leaving_date || '-',
                        ctc: vd.ctc || vd.salary || '-',
                        rm_name: vd.rm_name || vd.reporting_manager || vd.supervisor || '-',
                        rm_email: vd.rm_email || '-',
                        rm_phone: vd.rm_phone || '-',
                        _fromChecks: true,
                        _checkData: vd
                      };
                    }).filter(e => Object.keys(e._checkData).length > 0);
                  }
                  
                  return employmentData.length > 0 ? (
                    employmentData.map((emp, idx) => (
                    <Card key={idx}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {emp.employer_name || emp.company_name || `Employment ${idx + 1}`}
                          {emp._fromChecks && <span className="text-xs text-slate-400 font-normal">(from verification)</span>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div><span className="text-slate-500">Company:</span><br/><span className="font-medium">{emp.employer_name || emp.company_name || '-'}</span></div>
                          <div><span className="text-slate-500">Designation:</span><br/><span className="font-medium">{emp.designation || '-'}</span></div>
                          <div><span className="text-slate-500">Employee ID:</span><br/><span className="font-medium">{emp.employee_code || emp.employee_id || '-'}</span></div>
                          <div><span className="text-slate-500">Duration:</span><br/><span className="font-medium">{formatDateDDMonYYYY(emp.date_of_joining || emp.employed_from || emp.from_date || emp.start_date) || emp.date_of_joining || emp.from_date || '-'} to {(emp.date_of_relieving || emp.employed_to || emp.to_date || emp.end_date) ? (formatDateDDMonYYYY(emp.date_of_relieving || emp.employed_to || emp.to_date || emp.end_date) || emp.to_date) : (emp.is_current ? 'Present' : '-')}</span></div>
                          <div><span className="text-slate-500">CTC/Salary:</span><br/><span className="font-medium">{emp.salary || emp.ctc || '-'}</span></div>
                          <div><span className="text-slate-500">Reporting Manager:</span><br/><span className="font-medium">{emp.rm_name || emp.reporting_manager_name || '-'}</span></div>
                          <div><span className="text-slate-500">RM Email:</span><br/><span className="font-medium">{emp.rm_email || emp.reporting_manager_email || '-'}</span></div>
                          <div><span className="text-slate-500">RM Phone:</span><br/><span className="font-medium">{emp.rm_contact || emp.rm_phone || emp.reporting_manager_phone || '-'}</span></div>
                          {(emp.hr_name || emp.hr_email) && (
                            <>
                              <div><span className="text-slate-500">HR Name:</span><br/><span className="font-medium">{emp.hr_name || '-'}</span></div>
                              <div><span className="text-slate-500">HR Email:</span><br/><span className="font-medium">{emp.hr_email || '-'}</span></div>
                              <div><span className="text-slate-500">HR Phone:</span><br/><span className="font-medium">{emp.hr_phone || '-'}</span></div>
                            </>
                          )}
                        </div>
                        {emp._checkData && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-slate-500 mb-2 font-medium">Verification Details</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                              {Object.entries(emp._checkData)
                                .filter(([k, v]) => v && !['verification_submitted','_id','company_name','organization_name','designation','position','employee_id','employee_code','from_date','to_date','doj','dol','joining_date','leaving_date','ctc','salary','rm_name','rm_email','rm_phone','reporting_manager','supervisor'].includes(k))
                                .slice(0, 9)
                                .map(([k, v]) => (
                                  <div key={k}><span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}:</span><br/><span className="font-medium">{String(v)}</span></div>
                                ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardContent className="text-center py-8 text-slate-500">
                      <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No employment information available
                    </CardContent>
                  </Card>
                );
                })()}
              </TabsContent>
              
              {/* Education Tab */}
              <TabsContent value="education" className="space-y-4 mt-4">
                {(() => {
                  let educationData = viewEmployee.education_entries || viewEmployee.education_history || [];
                  
                  // If highest_education exists but education_entries is stale, prefer data entry format
                  if (viewEmployee.highest_education && (!educationData.length || !educationData.some(e => e.roll_no || e.class_degree))) {
                    educationData = [viewEmployee.highest_education, ...(viewEmployee.prior_educations || [])];
                  }
                  
                  // Fallback to verified_details from education checks
                  if (educationData.length === 0 && viewCase?.checks) {
                    const eduChecks = viewCase.checks.filter(c => c.check_type === 'education_check');
                    educationData = eduChecks.map(check => {
                      const vd = check.verified_details || check.subcheck_data || {};
                      return {
                        institution_name: vd.institution_name || vd.university || vd.college_name || check.sub_option || '-',
                        degree: vd.degree || vd.qualification || '-',
                        specialization: vd.specialization || vd.field_of_study || '-',
                        from_year: vd.from_year || vd.start_year || '-',
                        to_year: vd.to_year || vd.end_year || vd.year_of_passing || '-',
                        roll_number: vd.roll_number || vd.enrollment_number || '-',
                        percentage: vd.percentage || vd.cgpa || '-',
                        _fromChecks: true,
                        _checkData: vd
                      };
                    }).filter(e => Object.keys(e._checkData).length > 0);
                  }
                  
                  return educationData.length > 0 ? (
                    educationData.map((edu, idx) => (
                    <Card key={idx}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {edu.institution_name || edu.college || edu.university || `Education ${idx + 1}`}
                          {edu._fromChecks && <span className="text-xs text-slate-400 font-normal">(from verification)</span>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div><span className="text-slate-500">Institution:</span><br/><span className="font-medium">{edu.institution_name || edu.college || edu.university || '-'}</span></div>
                          <div><span className="text-slate-500">Course/Degree:</span><br/><span className="font-medium">{edu.course || edu.degree || edu.qualification || edu.education_type || '-'}</span></div>
                          <div><span className="text-slate-500">Specialization:</span><br/><span className="font-medium">{edu.specialization || edu.field_of_study || '-'}</span></div>
                          <div><span className="text-slate-500">Year of Passing:</span><br/><span className="font-medium">{edu.year_of_passing || edu.to_year || edu.end_year || '-'}</span></div>
                          <div><span className="text-slate-500">Roll Number:</span><br/><span className="font-medium">{edu.roll_no || edu.roll_number || edu.enrollment_number || '-'}</span></div>
                          <div><span className="text-slate-500">Percentage/CGPA:</span><br/><span className="font-medium">{edu.percentage || edu.cgpa || edu.class_degree || '-'}</span></div>
                        </div>
                        {edu._checkData && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-slate-500 mb-2 font-medium">Verification Details</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                              {Object.entries(edu._checkData)
                                .filter(([k, v]) => v && !['verification_submitted','_id','institution_name','university','college_name','degree','qualification','specialization','field_of_study','from_year','to_year','start_year','end_year','year_of_passing','roll_number','enrollment_number','percentage','cgpa'].includes(k))
                                .slice(0, 9)
                                .map(([k, v]) => (
                                  <div key={k}><span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}:</span><br/><span className="font-medium">{String(v)}</span></div>
                                ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardContent className="text-center py-8 text-slate-500">
                      <GraduationCap className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No education information available
                    </CardContent>
                  </Card>
                );
                })()}
              </TabsContent>
              
              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Uploaded Documents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CaseDocumentsList viewEmployee={viewEmployee} viewCase={viewCase} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Case Info Tab */}
              <TabsContent value="case" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Case Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><span className="text-slate-500">Case ID:</span><br/><span className="font-medium font-mono">{viewCase.id}</span></div>
                      <div><span className="text-slate-500">Request Raised:</span><br/><span className="font-medium">{(viewCase.invitation_sent_at || viewCase.created_at) ? new Date(viewCase.invitation_sent_at || viewCase.created_at).toLocaleDateString() : '-'}</span></div>
                      <div><span className="text-slate-500">Submitted:</span><br/><span className="font-medium">{viewCase.submitted_at ? new Date(viewCase.submitted_at).toLocaleDateString() : ((viewCase.employee_submitted || viewCase.source === 'bulk_import' || viewCase.source === 'data_entry') ? new Date(viewCase.created_at).toLocaleDateString() : 'Pending')}</span></div>
                      <div><span className="text-slate-500">Source:</span><br/><span className="font-medium capitalize">{viewCase.source || 'Manual'}</span></div>
                      <div><span className="text-slate-500">Client:</span><br/><span className="font-medium">{getCaseDetails(viewCase).client?.company_name || '-'}</span></div>
                      <div><span className="text-slate-500">Entity:</span><br/><span className="font-medium">{getCaseDetails(viewCase).entity?.entity_name || '-'}</span></div>
                      <div><span className="text-slate-500">Status:</span><br/>{(() => { const ds = getDisplayStatus(viewCase); const or = getOverallResult(viewCase, viewCase.checks); const ss = ds === 'completed' ? getCompletedStatusStyle(or) : STATUS_CONFIG[ds]; return <Badge data-testid="case-info-status-badge" className={ss?.bg}>{ss?.label}</Badge>; })()}</div>
                      <div><span className="text-slate-500">Overall Result:</span><br/><Badge className={RESULT_CONFIG[getOverallResult(viewCase, viewCase.checks)]?.bg}>{RESULT_CONFIG[getOverallResult(viewCase, viewCase.checks)]?.label}</Badge></div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sent Reports — per-dispatch ledger; double-tick on updated resends */}
                <Card data-testid="report-email-history-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-600" /> Sent Reports
                      {reportEmailHistory.length > 0 && (
                        <span className="text-xs text-slate-500 font-normal">({reportEmailHistory.length})</span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reportEmailHistoryLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading email history…</div>
                    ) : reportEmailHistory.length === 0 ? (
                      <p className="text-sm text-slate-500">No report emails sent yet.</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Sent At</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>To</TableHead>
                            <TableHead>Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportEmailHistory.map((row) => (
                            <TableRow key={row.id} data-testid={`report-email-row-${row.id}`}>
                              <TableCell>
                                {row.is_updated_report ? (
                                  <span title="Updated report resend"><CheckCheck data-testid={`report-email-double-tick-${row.id}`} className="w-4 h-4 text-blue-600" /></span>
                                ) : (
                                  <span title="Initial report send"><Check className="w-4 h-4 text-green-600" /></span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs">
                                {row.sent_at ? new Date(row.sent_at).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell className="text-xs max-w-md truncate" title={row.subject}>
                                {row.subject || '-'}
                              </TableCell>
                              <TableCell className="text-xs">{row.to_email || '-'}</TableCell>
                              <TableCell>
                                <Badge className={row.is_updated_report ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}>
                                  {row.is_updated_report ? 'Updated' : (row.report_type || 'Report')}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Verification Checks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Check Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead>Insufficiency</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewCase.checks?.map((check, idx) => {
                          const checkData = (check.verified_details && Object.keys(check.verified_details).length > 0)
                            ? check.verified_details
                            : check.subcheck_data || {};
                          const hasData = Object.keys(checkData).length > 0;
                          return (
                            <React.Fragment key={idx}>
                              <TableRow>
                                <TableCell className="font-medium">
                                  {CHECK_NAMES[check.check_type] || check.check_type}
                                  {check.sub_option && <span className="text-xs text-slate-400 ml-1 capitalize">({check.sub_option.replace(/_/g, ' ')})</span>}
                                </TableCell>
                                <TableCell>
                                  <Badge className={
                                    check.status === 'verified' || check.status === 'clear' ? 'bg-emerald-100 text-emerald-700' :
                                    check.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                    check.status === 'discrepancy' ? 'bg-rose-100 text-rose-700' :
                                    'bg-slate-100 text-slate-700'
                                  }>
                                    {check.status || 'Pending'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={
                                    (check.verification_result || check.result) === 'clear' ? 'bg-emerald-100 text-emerald-700' :
                                    (check.verification_result || check.result) === 'discrepancy' || (check.verification_result || check.result) === 'major_discrepancy' ? 'bg-rose-100 text-rose-700' :
                                    (check.verification_result || check.result) === 'minor_discrepancy' ? 'bg-amber-100 text-amber-700' :
                                    'bg-slate-100 text-slate-700'
                                  }>
                                    {check.verification_result || check.result || 'Pending'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {check.has_insufficiency ? (
                                    <Badge className="bg-amber-100 text-amber-700">
                                      <AlertTriangle className="w-3 h-3 mr-1" />Yes
                                    </Badge>
                                  ) : (
                                    <span className="text-emerald-600">✓ Clear</span>
                                  )}
                                </TableCell>
                              </TableRow>
                              {hasData && (
                                <TableRow>
                                  <TableCell colSpan={4} className="bg-slate-50 py-2 px-4">
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                      {Object.entries(checkData)
                                        .filter(([k, v]) => v && !['verification_submitted','_id'].includes(k))
                                        .slice(0, 6)
                                        .map(([k, v]) => (
                                          <div key={k}>
                                            <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}: </span>
                                            <span className="font-medium">{String(v).length > 50 ? String(v).slice(0, 50) + '...' : String(v)}</span>
                                          </div>
                                        ))}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Audit Trail Tab */}
              <TabsContent value="activity" className="space-y-4 mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <AuditTrail caseId={viewCase?.id || viewCase?.case_id} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setViewCase(null); setViewEmployee(null); setActivityLog([]); }}>
              Close
            </Button>
            {viewCase && (
              <>
                <Button variant="outline" onClick={() => generateReport(viewCase, 'interim')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Interim Report
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => generateReport(viewCase, 'final')}
                  disabled={!['completed', 'final_report_qc', 'report_approved'].includes(viewCase.status)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Final Report
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete {selectedCases.size} Case(s)</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {(() => {
              const selected = cases.filter(c => selectedCases.has(c.id));
              const activeCases = selected.filter(c => 
                c.status && !['initiated', 'draft'].includes(c.status)
              );
              return (
                <>
                  {activeCases.length > 0 && (
                    <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-4">
                      <p className="text-sm font-semibold text-amber-800 mb-2">
                        Warning: {activeCases.length} case(s) have an active status!
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {activeCases.map(c => (
                          <div key={c.id} className="text-xs text-amber-700 flex items-center gap-2">
                            <span className="font-mono">{c.id}</span>
                            <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-medium">
                              {(c.status || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-sm text-slate-600 mb-3">
                    This will permanently delete the selected cases and ALL related data:
                  </p>
                  <ul className="text-sm text-slate-500 list-disc ml-5 space-y-1">
                    <li>Verification cases & all checks</li>
                    <li>Employee records & user accounts</li>
                    <li>Audit trail logs</li>
                    <li>Insufficiency records</li>
                    <li>All dashboard references</li>
                  </ul>
                  <p className="text-sm font-medium text-red-600 mt-3">This action cannot be undone.</p>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete} 
              disabled={deleting}
              data-testid="confirm-bulk-delete-btn"
            >
              {deleting ? 'Deleting...' : `Delete ${selectedCases.size} Case(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Report Confirmation Dialog */}
      <Dialog open={bulkSendConfirmOpen} onOpenChange={setBulkSendConfirmOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-blue-700">Send Report Email for {selectedCases.size} Case(s)</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-3">
              This will generate and email the BGV report (Final/Interim auto-detected) for each selected case to the respective client HR email with CC to admin.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3 max-h-40 overflow-y-auto">
              {cases.filter(c => selectedCases.has(c.id)).map(c => {
                const { employee, client } = getCaseDetails(c);
                return (
                  <div key={c.id} className="text-xs text-blue-800 flex items-center justify-between py-1 border-b border-blue-100 last:border-0">
                    <span className="font-mono">{c.id}</span>
                    <span className="text-blue-600">{employee?.name || c.employee_name || 'N/A'}</span>
                    <span className="text-slate-500">{client?.company_name || c.client_name || '-'}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSendConfirmOpen(false)} disabled={bulkSending}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkSendReport} 
              disabled={bulkSending}
              data-testid="confirm-bulk-send-report-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send {selectedCases.size} Report(s)</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stop Check Dialog */}
      <Dialog open={!!showStopCheck} onOpenChange={(o) => { if (!o) setShowStopCheck(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StopCircle className="w-5 h-5 text-rose-600" />
              Stop Check — {showStopCheck?.id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
              This will stop all in-progress checks and move the case to <strong>Stopped</strong>. Completed checks will be preserved.
            </div>
            <div>
              <Label className="text-sm">Reason *</Label>
              <Select value={stopReason} onValueChange={setStopReason}>
                <SelectTrigger data-testid="allcases-stop-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                  <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Description (optional)</Label>
              <Textarea
                value={stopDescription}
                onChange={(e) => setStopDescription(e.target.value)}
                placeholder="Additional context..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStopCheck(null)} disabled={stopping}>Cancel</Button>
            <Button
              onClick={handleStopCheck}
              disabled={!stopReason || stopping}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="allcases-confirm-stop-check-btn"
            >
              {stopping ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Stopping...</> : <><StopCircle className="w-4 h-4 mr-2" />Stop Check</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ChangePackageModal
        open={!!changePkgCase}
        onOpenChange={(v) => { if (!v) setChangePkgCase(null); }}
        caseId={changePkgCase?.id}
        currentPackageId={changePkgCase?.package_id}
        onChanged={() => { setChangePkgCase(null); fetchData(); }}
      />
    </div>
  );
}
